import Joi from 'joi';
import { appRoles, appRoleSets, appRoleSetTypes } from 'services/Auth/appRoles';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { Role } from './auth/role';
import { IdentityProviderType } from './identityProvider';

export enum UserTypes {
  Member = 0,
  Guest = 1,
  Application = 2,
}

export default class User {
  id: string;

  tenantId?: string;

  name: string;

  email: string;

  created?: Date;

  lastLogin?: Date;

  hasLicense: boolean = false;

  userType?: UserTypes;

  roles?: Role[];

  groups?: string[];

  userTimeZoneCode?: string;

  userLanguageCode?: string;

  systemRoleIds?: number[];

  //the identity behind this user: the provider and its own id for the account (the object id for Entra)
  identityProviderType: IdentityProviderType;

  providerUserId?: string;

  eventSubscriptionId?: string;

  eventSubscriptionExp?: Date;

  eventSubscriptionSync?: boolean;

  // internal props

  photo: Blob | undefined;

  photoURL: string | undefined;

  constructor(id: string, email: string, name: string) {
    this.id = id;
    this.name = name;
    this.email = email;
    this.userType = UserTypes.Member;
    this.identityProviderType = IdentityProviderType.Entra;
  }

  clone(): User {
    const newUser = new User(this.id, this.email, this.name);
    newUser.tenantId = this.tenantId;
    newUser.userType = this.userType;
    newUser.identityProviderType = this.identityProviderType;
    newUser.providerUserId = this.providerUserId;
    newUser.created = this.created;
    newUser.lastLogin = this.lastLogin;
    newUser.hasLicense = this.hasLicense;
    newUser.userTimeZoneCode = this.userTimeZoneCode;
    newUser.userLanguageCode = this.userLanguageCode;
    newUser.groups = this.groups ? [...this.groups] : [];
    newUser.roles = this.roles?.map((r) => r.clone());
    newUser.systemRoleIds = this.systemRoleIds ? [...this.systemRoleIds] : [];
    newUser.photo = this.photo?.slice();
    newUser.photoURL = this.photoURL;
    newUser.eventSubscriptionId = this.eventSubscriptionId;
    newUser.eventSubscriptionExp = this.eventSubscriptionExp;
    newUser.eventSubscriptionSync = this.eventSubscriptionSync;

    return newUser;
  }

  static getEmptyUser(id?: string) {
    const newUser = new User('', '', '');
    if (id) newUser.id = id;

    return newUser;
  }

  get systemRoles(): number[] {
    return this.roles?.filter((r) => r.systemRoleId !== undefined).map((r) => r.systemRoleId as number) ?? [];
  }

  hasPaidLicense = (): boolean => {
    const sr = this.systemRoleIds ?? [];

    for (let idx = 0; idx < sr.length; idx++) {
      if (appRoleSets[appRoleSetTypes.Licensed].includes(sr[idx])) {
        return true;
      }
    }

    return false;
  };

  isAdmin = (): boolean => {
    const sr = this.systemRoleIds ?? [];

    return sr.includes(appRoles.Admin);
  };

  getPhotoURLObj(): string | undefined {
    if (this.photo) {
      if (!this.photoURL) {
        this.photoURL = URL.createObjectURL(this.photo);
      }

      return this.photoURL;
    }

    return undefined;
  }

  //idRequired: false for a user invited by e-mail into an organization of a non-Microsoft identity provider;
  //the API assigns the id then, and the first sign-in links it to the account
  validate(localizedFields: Record<string, string>, options?: { idRequired?: boolean }): Joi.ValidationResult {
    const idRequired = options?.idRequired ?? true;
    const schema: Joi.ObjectSchema = Joi.object({
      id: idRequired
        ? Joi.string().guid().required().label(localizedFields['id'])
        : Joi.string().allow('').label(localizedFields['id']),
      name: Joi.string().max(512).required().label(localizedFields['name']),
      email: Joi.string()
        .email({ tlds: { allow: false } })
        .max(512)
        .required()
        .label(localizedFields['email']),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate({ id: this.id, name: this.name, email: this.email }, { abortEarly: false });
  }

  isAppType(): boolean {
    return this.userType === UserTypes.Application;
  }
}

/** The name to show for a user: their own name, or their email when it is blank. */
export function getUserDisplayName(user: User): string {
  return user.name && user.name.length > 0 ? user.name : user.email;
}
